/**
 * assembleAssessmentText — the finished care-management assessment report,
 * flattened to plain text for a DRAFT case note (the drafter's
 * "File to the client's record" action, founder ruling 2026-07-06).
 *
 * The body is the CASE-NOTE text: what the assessment door re-extracts to
 * seed the record, and what a care manager reads on the posted note. It is
 * NOT the .docx deliverable — so the memo chrome (TO / FROM / the signature
 * block) is deliberately omitted: those are delivery formalities, noise for
 * the record and a distraction for extraction. What ships is the clinical
 * substance — every review section, the ADL/IADL table, and the unified
 * numbered recommendations (drafted + CM-inserted, one list, per the
 * 2026-07-06 numbering ruling).
 *
 * Section order mirrors AssessmentDraftOutput's SECTION_ORDER (the same
 * canonical order the CM reviewed on screen); the type's field order is the
 * contract. Empty sections are skipped so a sparse report reads clean.
 */

import type {
  AssessmentDraftResponse,
  AssessmentReportSections,
  AdlIadlEntry,
  MedicalAssessmentSection,
} from './api';

/** Canonical review-section order + human titles (mirror of the output view). */
const SECTION_ORDER: Array<[keyof AssessmentReportSections, string]> = [
  ['history', 'History'],
  ['medical_assessment', 'Medical Assessment'],
  ['neurological', 'Neurological'],
  ['sensory', 'Sensory'],
  ['sleep', 'Sleep'],
  ['pain', 'Pain'],
  ['cardiovascular', 'Cardiovascular'],
  ['respiratory', 'Respiratory'],
  ['gastroenterological', 'Gastroenterological'],
  ['urinary', 'Urinary'],
  ['musculoskeletal', 'Musculoskeletal'],
  ['integumentary', 'Integumentary'],
  ['endocrine', 'Endocrine'],
  ['functional_assessment', 'Functional Assessment'],
  ['social_assessment', 'Social Assessment'],
  ['home_safety_assessment', 'Home Safety Assessment'],
  ['summary_and_recommendations', 'Summary and Recommendations'],
];

const NEURO_SUBS: Array<[keyof AssessmentReportSections['neurological'], string]> = [
  ['presentation', 'Presentation'],
  ['memory_cognition', 'Memory/Cognition'],
  ['mood_behavior', 'Mood and Behavior'],
  ['neuro_medical', 'Neuro-medical'],
];

// Prose sub-sections only (the v8 medications/providers table rows render
// via their own block below, not through this list).
const MED_SUBS: Array<
  [
    keyof Pick<
      MedicalAssessmentSection,
      | 'home_care_status'
      | 'past_surgical_history'
      | 'recent_hospitalization_history'
      | 'insurance'
      | 'pharmacy'
    >,
    string,
  ]
> = [
  ['home_care_status', 'Home Care Status'],
  ['past_surgical_history', 'Past Surgical History'],
  ['recent_hospitalization_history', 'Recent Hospitalization History'],
  ['insurance', 'Insurance'],
  ['pharmacy', 'Pharmacy'],
];

function has(text: string | undefined | null): text is string {
  return typeof text === 'string' && text.trim().length > 0;
}

function medicalBlock(med: MedicalAssessmentSection): string {
  const parts: string[] = [];
  // The overview leads. On a LEGACY draft its "Current Medications:" /
  // "Current Providers:" lead-in lines ride through verbatim (they carry
  // the lists); a v8 draft carries the lists as the structured rows below.
  if (has(med.overview)) parts.push(med.overview.trim());
  // Meds/providers tables (prompt v8): one "- a | b | c" line per row,
  // empty cells dropped. Empty/absent arrays (legacy) add nothing.
  const tableLines = (title: string, rows: ReadonlyArray<object> | undefined): void => {
    if (!rows || rows.length === 0) return;
    const lines = rows.map(
      (r) =>
        `- ${Object.values(r)
          .filter((c) => typeof c === 'string' && c.trim().length > 0)
          .join(' | ')}`,
    );
    parts.push(`${title}\n${lines.join('\n')}`);
  };
  tableLines('Current Medications', med.medications);
  tableLines('Current Providers', med.providers);
  for (const [key, title] of MED_SUBS) {
    const v = med[key];
    if (has(v)) parts.push(`${title}\n${v.trim()}`);
  }
  return parts.join('\n\n');
}

function neuroBlock(neuro: AssessmentReportSections['neurological']): string {
  const parts: string[] = [];
  for (const [key, title] of NEURO_SUBS) {
    const v = neuro[key];
    if (has(v)) parts.push(`${title}\n${v.trim()}`);
  }
  return parts.join('\n\n');
}

/** The ADL/IADL table as lines: "Activity: Level — notes". */
function adlBlock(rows: AdlIadlEntry[]): string {
  return rows
    .map((r) => {
      const base = `${r.activity}: ${r.level}`;
      return has(r.notes) ? `${base}. ${r.notes.trim()}` : base;
    })
    .join('\n');
}

function summaryBlock(
  section: AssessmentReportSections['summary_and_recommendations'],
  insertedRecommendations: ReadonlyArray<{ topic: string; text: string }>,
): string {
  const parts: string[] = [];
  if (has(section.synthesis)) parts.push(section.synthesis.trim());
  // Drafted + inserted recommendations = ONE continuous numbered list
  // (2026-07-06 unified-numbering ruling), drafted first, insert order after.
  const items: Array<{ label: string; body: string }> = [
    ...section.recommendations.map((r) => ({ label: r.label, body: r.body })),
    ...insertedRecommendations.map((r) => ({ label: r.topic, body: r.text })),
  ];
  items.forEach((it, i) => {
    const label = has(it.label) ? `${it.label.trim()}. ` : '';
    parts.push(`${i + 1}. ${label}${it.body.trim()}`);
  });
  return parts.join('\n\n');
}

/**
 * Flatten a Care Management assessment report to a case-note body.
 * `insertedRecommendations` = the CM's clicked-in vetted recommendations
 * (topic + text), appended to the drafted list in one numbered sequence.
 */
export function assembleAssessmentText(
  response: AssessmentDraftResponse,
  insertedRecommendations: ReadonlyArray<{ topic: string; text: string }> = [],
): string {
  const blocks: string[] = [];

  const title = has(response.header.re) ? response.header.re.trim() : 'Initial Care Assessment';
  blocks.push(title);

  for (const [key, heading] of SECTION_ORDER) {
    let body = '';
    if (key === 'medical_assessment') {
      body = medicalBlock(response.sections.medical_assessment);
    } else if (key === 'neurological') {
      body = neuroBlock(response.sections.neurological);
    } else if (key === 'functional_assessment') {
      // The functional narrative leads; the ADL/IADL table follows it.
      const narrative = response.sections.functional_assessment;
      const table = response.adl_iadl.length > 0 ? adlBlock(response.adl_iadl) : '';
      body = [has(narrative) ? narrative.trim() : '', table].filter((s) => s.length > 0).join('\n\n');
    } else if (key === 'summary_and_recommendations') {
      body = summaryBlock(response.sections.summary_and_recommendations, insertedRecommendations);
    } else {
      const v = response.sections[key];
      if (typeof v === 'string') body = v.trim();
    }
    if (body.length > 0) blocks.push(`${heading.toUpperCase()}\n${body}`);
  }

  return blocks.join('\n\n');
}
